import { Check, Layers3, LayoutGrid, List, Sparkles } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

type VariantId = "atlas" | "command" | "signal" | "pathways";

type ModuleGroup = {
  name: string;
  description: string;
  items: readonly string[];
  accent: string;
};

const groups: readonly ModuleGroup[] = [
  {
    name: "People",
    description: "The people and records behind every school day.",
    accent: "#156064",
    items: ["Students", "Guardians", "Staff", "Classes", "Streams", "Subjects", "Enrolment", "Promotion", "Spreadsheet import", "Qualifications", "Assignments"],
  },
  {
    name: "Fees",
    description: "A clean trail from fee structure to receipt.",
    accent: "#00876a",
    items: ["Fee items", "Fee structures", "Discounts", "Bursaries", "Invoices", "Payments", "Receipts", "Ledger", "Statements", "Arrears"],
  },
  {
    name: "Academics",
    description: "Marks, grading and reports in one academic record.",
    accent: "#a07b14",
    items: ["Attendance", "Assessments", "Marks", "Grading schemes", "Grade bands", "Aggregates", "Divisions", "Positions", "Report cards", "Results"],
  },
  {
    name: "Timetable",
    description: "Put the right class, teacher and room together.",
    accent: "#d9673d",
    items: ["Period structures", "Rooms", "Versions", "Slot placement", "Conflict checks", "Teaching load", "Substitutions"],
  },
  {
    name: "Messaging",
    description: "Reach parents with the right message and cost.",
    accent: "#156064",
    items: ["Templates", "Campaigns", "Audiences", "Cost preview", "Credit ledger", "Send queue"],
  },
  {
    name: "Platform",
    description: "The controls that keep the whole system dependable.",
    accent: "#00876a",
    items: ["Tenants", "Module toggles", "Academic calendar", "Audit log", "Number sequences", "Desktop till", "Parent portal"],
  },
];

const variants: readonly { id: VariantId; number: string; name: string; description: string; mood: string }[] = [
  { id: "atlas", number: "01", name: "Atlas Grid", description: "Clear cards with more breathing room and a stronger visual hierarchy.", mood: "Calm / structured" },
  { id: "command", number: "02", name: "Command Board", description: "A more premium operational view with counts, status and fast scanning.", mood: "Focused / premium" },
  { id: "signal", number: "03", name: "Signal List", description: "Shorter rows and stronger labels for a compact, information-rich section.", mood: "Efficient / precise" },
  { id: "pathways", number: "04", name: "School Pathways", description: "A warmer story-led layout that explains why each group matters.", mood: "Human / welcoming" },
];

export function ModuleVariantsPage() {
  const [active, setActive] = useState<VariantId>("atlas");
  const selected = variants.find((variant) => variant.id === active) ?? variants[0]!;

  return (
    <div className="min-h-screen bg-[#eef1e9] text-[#12201f]">
      <header className="border-b border-[#0e4e4d]/20 bg-[#156064] text-white">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 py-4 lg:px-8">
          <Link to="/" className="font-display text-xl font-bold tracking-tight">
            Waka<span className="text-[#f8e16c]">skul</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-white/70 sm:inline">Modules section review</span>
            <Link to="/" className="rounded border border-white/30 px-3 py-2 font-medium hover:bg-white/10">
              Back to site
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-5 py-8 lg:px-8 lg:py-12">
        <div className="mb-8 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <div>
            <p className="font-mono text-[0.65rem] font-bold uppercase tracking-[0.2em] text-[#156064]">WakaSkul / section study</p>
            <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold tracking-[-0.04em] sm:text-5xl">How should the modules feel?</h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-black/60 sm:text-lg">Four ways to organize the same 51 features. Choose the version that feels easiest for a director or bursar to scan.</p>
          </div>
          <div className="rounded border border-black/10 bg-white/70 px-4 py-3 text-sm text-black/60"><span className="font-bold text-black">Selected:</span> {selected.name}</div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" role="tablist" aria-label="Modules section variants">
          {variants.map((variant) => (
            <button
              key={variant.id}
              type="button"
              role="tab"
              aria-selected={variant.id === active}
              onClick={() => setActive(variant.id)}
              className={`rounded border p-4 text-left transition ${variant.id === active ? "border-[#156064] bg-white shadow-[0_14px_34px_rgba(18,32,31,0.12)]" : "border-black/10 bg-white/45 hover:-translate-y-0.5 hover:bg-white"}`}
            >
              <span className="font-mono text-xs font-bold text-black/40">{variant.number}</span>
              <p className="mt-3 font-display text-base font-bold">{variant.name}</p>
              <p className="mt-1 text-xs leading-relaxed text-black/55">{variant.description}</p>
              <p className="mt-4 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-[#156064]">{variant.mood}</p>
            </button>
          ))}
        </div>

        <div className="mt-8 overflow-hidden rounded border border-black/10 bg-white shadow-[0_24px_70px_rgba(18,32,31,0.13)]">
          <div className="flex items-center justify-between border-b border-black/10 px-5 py-3 text-xs text-black/45 lg:px-7">
            <span className="font-mono">LIVE MODULE PREVIEW</span>
            <span className="hidden sm:inline">Select a direction above</span>
          </div>
          {active === "atlas" && <Atlas />}
          {active === "command" && <Command />}
          {active === "signal" && <Signal />}
          {active === "pathways" && <Pathways />}
        </div>
      </main>
    </div>
  );
}

function Atlas() {
  return (
    <section className="bg-[#f3f5ef] px-5 py-10 sm:px-10 lg:px-16 lg:py-14">
      <VariantIntro icon={<LayoutGrid className="h-4 w-4" />} eyebrow="Everything connected" title="A clear map of the whole school." body="Give every module a home, then let the school team scan the labels without decoding a wall of chips." />
      <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => <AtlasCard key={group.name} group={group} />)}
      </div>
    </section>
  );
}

function AtlasCard({ group }: { group: ModuleGroup }) {
  return <article className="rounded border border-[#c8d2c2] bg-white p-5 shadow-[0_10px_28px_rgba(18,32,31,0.06)]"><div className="flex items-start justify-between gap-3"><div><p className="font-mono text-[0.6rem] font-bold uppercase tracking-[0.17em]" style={{ color: group.accent }}>{group.name}</p><p className="mt-2 text-sm leading-relaxed text-black/55">{group.description}</p></div><span className="tabular font-display text-2xl font-bold text-black/15">{String(group.items.length).padStart(2, "0")}</span></div><ul className="mt-5 flex flex-wrap gap-1.5">{group.items.map((item) => <li key={item} className="rounded border border-[#d5ddd0] bg-[#f7f9f3] px-2 py-1 text-xs text-[#233433]">{item}</li>)}</ul></article>;
}

function Command() {
  return (
    <section className="bg-[#101b1d] px-5 py-10 text-white sm:px-10 lg:px-16 lg:py-14">
      <VariantIntro dark icon={<Sparkles className="h-4 w-4" />} eyebrow="The school operating system" title="See the signal before the detail." body="A premium command board for leaders who want to know what needs attention, then open the exact module." />
      <div className="mt-10 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group, index) => <article key={group.name} className="rounded border border-white/10 bg-white/[0.04] p-5"><div className="flex items-center justify-between"><p className="font-mono text-[0.6rem] font-bold uppercase tracking-[0.17em]" style={{ color: group.accent }}>{group.name}</p><span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#c9f76f] text-xs font-bold text-[#101b1d]">{index + 1}</span></div><p className="mt-5 font-display text-2xl font-bold">{group.items.length} areas</p><p className="mt-1 text-sm text-white/55">Ready to open and manage.</p><div className="mt-5 h-1 rounded bg-white/10"><div className="h-full rounded bg-[#c9f76f]" style={{ width: `${56 + group.items.length * 3}%` }} /></div></article>)}
      </div>
    </section>
  );
}

function Signal() {
  return (
    <section className="bg-white px-5 py-10 sm:px-10 lg:px-16 lg:py-14">
      <VariantIntro icon={<List className="h-4 w-4" />} eyebrow="Built for fast scanning" title="Short lists. Strong signals." body="A compact layout for teams that already know the product and want to get to the right screen quickly." />
      <div className="mt-10 divide-y divide-[#dce3d6] border-y border-[#dce3d6]">{groups.map((group) => <div key={group.name} className="grid gap-4 py-5 md:grid-cols-[10rem_1fr] md:items-start"><div><p className="font-mono text-[0.6rem] font-bold uppercase tracking-[0.17em]" style={{ color: group.accent }}>{group.name}</p><p className="mt-1 text-xs text-black/45">{group.items.length} features</p></div><ul className="grid gap-x-5 gap-y-2 text-sm text-black/65 sm:grid-cols-2 lg:grid-cols-4">{group.items.map((item) => <li key={item} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-[#156064]" />{item}</li>)}</ul></div>)}</div>
    </section>
  );
}

function Pathways() {
  return (
    <section className="bg-[#fffaf0] px-5 py-10 sm:px-10 lg:px-16 lg:py-14">
      <VariantIntro icon={<Layers3 className="h-4 w-4" />} eyebrow="A school in motion" title="Follow the work from people to platform." body="Organize the modules around the questions a school team asks every day, not only around the database underneath." />
      <div className="mt-10 grid gap-4 lg:grid-cols-3">{groups.map((group, index) => <article key={group.name} className="rounded border border-[#e8ddc9] bg-white/75 p-5"><div className="flex items-center gap-3"><span className="font-mono text-xs font-bold text-black/35">0{index + 1}</span><span className="h-2 w-2 rounded-full" style={{ backgroundColor: group.accent }} /><h3 className="font-display text-xl font-bold">{group.name}</h3></div><p className="mt-4 text-sm leading-relaxed text-black/60">{group.description}</p><div className="mt-5 border-t border-[#e8ddc9] pt-4"><p className="font-mono text-[0.6rem] uppercase tracking-[0.14em] text-black/35">Includes</p><p className="mt-2 text-sm leading-relaxed text-black/70">{group.items.slice(0, 4).join(" · ")}</p><p className="mt-4 text-xs font-semibold text-[#156064]">+ {group.items.length - 4} more tools</p></div></article>)}</div>
    </section>
  );
}

function VariantIntro({ icon, eyebrow, title, body, dark = false }: { icon: ReactNode; eyebrow: string; title: string; body: string; dark?: boolean }) {
  return <div className="max-w-2xl"><p className={`flex items-center gap-2 font-mono text-[0.6rem] font-bold uppercase tracking-[0.18em] ${dark ? "text-[#c9f76f]" : "text-[#156064]"}`}>{icon}{eyebrow}</p><h2 className={`mt-4 font-display text-4xl font-bold leading-[0.98] tracking-[-0.04em] sm:text-6xl ${dark ? "text-white" : "text-[#12201f]"}`}>{title}</h2><p className={`mt-5 text-base leading-relaxed sm:text-lg ${dark ? "text-white/60" : "text-black/60"}`}>{body}</p></div>;
}
